import { useCallback, useEffect, useMemo, useState } from "react";
import moment from "moment";
import { useNavigate, useParams } from "react-router-dom";
import useVendorStore from "../../HomePage/useVendorStore";
import {
  PO_STATUS,
  SELLER_STATUS_APPROVED,
  STATUS_BEGIN,
  STATUS_DONE,
  STATUS_FAIL,
} from "../../../../utils/constants/others";
import { ACTION_TYPES } from "../../../../utils/constants/app_router";
import { handleActionType } from "../../../../helpers/app_router";
import { isPackingLabelsV2Enabled, needsPackagingDetails } from "../../EmbeddedUpc/embeddedUpcLabel";

const buildInitialPrices = (items) =>
  Object.fromEntries(items.map((item) => [item.id, String(Number(item.price_per_unit || 0))]));

export default function usePurchaseOrderDetailsLogic() {
  const navigate = useNavigate();
  const { source, purchaseOrderId } = useParams();
  const outletId = window.localStorage.getItem("outlet_id");

  const getPurchaseOrders = useVendorStore((state) => state.getPurchaseOrders);
  const getPurchaseOrderStatus = useVendorStore((state) => state.getPurchaseOrderStatus);
  const purchaseOrders = useVendorStore((state) => state.purchaseOrders);
  const approvePurchaseOrder = useVendorStore((state) => state.approvePurchaseOrder);
  const updateSellerApprovalStatus = useVendorStore((state) => state.updateSellerApprovalStatus);
  const submitPurchaseOrderDispute = useVendorStore((state) => state.submitPurchaseOrderDispute);
  const submitPurchaseOrderDisputeStatus = useVendorStore((state) => state.submitPurchaseOrderDisputeStatus);
  const getPackagingLabelComplianceDetails = useVendorStore((state) => state.getPackagingLabelComplianceDetails);

  const [showConfirmation, setShowConfirmation] = useState(false);
  const [accepted, setAccepted] = useState(false);
  const [showContactDetails, setShowContactDetails] = useState(false);

  const [showDisputeForm, setShowDisputeForm] = useState(false);
  const [showDisputeSubmittedModal, setShowDisputeSubmittedModal] = useState(false);
  const [proposedDeliveryDate, setProposedDeliveryDate] = useState("");
  const [proposedPrices, setProposedPrices] = useState({});
  const [remarks, setRemarks] = useState("");
  const [disputeError, setDisputeError] = useState("");

  const [upcModalItem, setUpcModalItem] = useState(null);
  // null = not yet determined, true/false once a product's compliance is fetched.
  const [packingLabelsV2Enabled, setPackingLabelsV2Enabled] = useState(null);
  const [checkingComplianceFor, setCheckingComplianceFor] = useState(null);
  const [complianceError, setComplianceError] = useState("");

  const fetchPoDetailsPageData = () => {
    getPurchaseOrders(
      {
        is_item_required: true,
        purchase_order_id: purchaseOrderId,
        fetch_active_product_variants: true,
        is_maker_checker_request_id_required: true,
      },
      true
    );
  }

  useEffect(() => {
    fetchPoDetailsPageData()
  }, [getPurchaseOrders, purchaseOrderId]);

  const purchaseOrder = useMemo(
    () => (purchaseOrders || []).find((po) => String(po.id) === String(purchaseOrderId)) || null,
    [purchaseOrders, purchaseOrderId]
  );
  const items = useMemo(() => purchaseOrder?.purchase_order_items || [], [purchaseOrder]);
  const totalWeight = useMemo(
    () => items.reduce((total, item) => total + Number(item.quantity_ordered || 0) * Number(item.weight_per_packet || 0), 0),
    [items]
  );

  const isLoading = getPurchaseOrderStatus === STATUS_BEGIN || (!purchaseOrder && getPurchaseOrderStatus !== STATUS_FAIL && getPurchaseOrderStatus !== STATUS_DONE);
  const hasError = !isLoading && !purchaseOrder;

  const isPending = purchaseOrder?.po_status === PO_STATUS.WAITING_FOR_VENDOR_APPROVAL;
  const canDispute = purchaseOrder?.po_status === PO_STATUS.WAITING_FOR_VENDOR_APPROVAL;
  const canAccept = (purchaseOrder?.po_status === PO_STATUS.WAITING_FOR_VENDOR_APPROVAL && purchaseOrder?.seller_status !== SELLER_STATUS_APPROVED);
  const canGenerateUpc = Boolean(
    purchaseOrder?.po_status === PO_STATUS.APPROVED &&
      purchaseOrder?.seller_status === SELLER_STATUS_APPROVED &&
      purchaseOrder?.should_generate_embedded_upc
  );

  const documentUrl = purchaseOrder?.po_document_url?.trim() || "";
  const phoneNumber = String(purchaseOrder?.created_by_phone_no || "").trim();
  const creatorName = purchaseOrder?.created_by_user_name || "PO creator";

  const goToList = () => handleActionType(ACTION_TYPES.SELLER_PO_LIST, {}, navigate);
  const goToPoDetails = useCallback(
    () => navigate(`/po/details/${source}/${purchaseOrderId}`),
    [navigate, source, purchaseOrderId]
  );

  const downloadPurchaseOrder = () => {
    if (window?.downloadFileMessageHandler) {
      window.downloadFileMessageHandler.postMessage(documentUrl);
      return;
    }
    window.open(documentUrl, "_blank", "noopener,noreferrer");
  };

  const callPurchaseOrderCreator = () => {
    window.location.href = `tel:+91${phoneNumber.replace(/\s/g, "")}`;
  };

  const acceptPurchaseOrder = () => {
    approvePurchaseOrder(
      { purchase_order_id: purchaseOrder.id, vendor_id: parseInt(outletId, 10) },
      () => {
        setAccepted(true);
        setShowConfirmation(false);
      }
    );
  };

  const openDispute = () => {
    setProposedDeliveryDate(
      purchaseOrder.expected_delivery_date
        ? moment(purchaseOrder.expected_delivery_date).format("YYYY-MM-DD")
        : ""
    );
    setProposedPrices(buildInitialPrices(items));
    setRemarks("");
    setDisputeError("");
    setShowDisputeForm(true);
  };

  const closeDispute = () => setShowDisputeForm(false);

  const setProposedPrice = (itemId, value) =>
    setProposedPrices((prices) => ({ ...prices, [itemId]: value }));

  const submitDispute = () => {
    const originalDeliveryDate = purchaseOrder?.expected_delivery_date
      ? moment(purchaseOrder.expected_delivery_date).format("YYYY-MM-DD")
      : "";
    const hasDateChanged = proposedDeliveryDate !== originalDeliveryDate;
    const hasPriceChanged = items.some(
      (item) => String(proposedPrices[item.id]) !== String(Number(item.price_per_unit || 0))
    );
    if (!hasDateChanged && !hasPriceChanged) {
      setDisputeError("Please change at least one value (delivery date or a price) before submitting a dispute.");
      return;
    }

    const isValid =
      Boolean(proposedDeliveryDate) &&
      items.length > 0 &&
      items.every((item) => Number(proposedPrices[item.id]) > 0);
    if (!isValid) {
      setDisputeError("Add a delivery date and a valid proposed price for every product.");
      return;
    }

    setDisputeError("");
    submitPurchaseOrderDispute(
      {
        purchase_order_id: purchaseOrder.id,
        proposed_delivery_date: proposedDeliveryDate,
        remarks: remarks.trim(),
        items: items.map((item) => ({
          purchase_order_item_id: item.id,
          product_number: item.product_number,
          proposed_price: Number(proposedPrices[item.id]),
        })),
      },
      () => {
        setShowDisputeForm(false);
        setShowDisputeSubmittedModal(true);
      }
    );
  };

  const onSuccessModalClose = () => {
    setShowDisputeSubmittedModal(false);
    fetchPoDetailsPageData();
  }
  const handleGenerateQr = (item) => {
    if (checkingComplianceFor) return;
    if (packingLabelsV2Enabled === false) {
      setUpcModalItem(item);
      return;
    }
    const returnTo = `/po/details/${source}/${purchaseOrderId}`;
    setComplianceError("");
    setCheckingComplianceFor(item?.product_number);
    getPackagingLabelComplianceDetails(
      { product_number: item?.product_number, vendor_id: outletId },
      (data) => {
        setCheckingComplianceFor(null);
        const requirements = data?.requirements || {};
        const vendorDetails = data?.vendor_details || {};
        if (!isPackingLabelsV2Enabled(requirements)) {
          setPackingLabelsV2Enabled(false);
          setUpcModalItem(item);
          return;
        }
        setPackingLabelsV2Enabled(true);
        if (needsPackagingDetails(requirements, vendorDetails)) {
          navigate("/download-labels/compliance", {
            state: { product: item, requirements, vendorDetails, returnTo },
          });
        } else {
          navigate("/download-labels/download", {
            state: { product: item, requirements, returnTo },
          });
        }
      },
      (errMsg) => {
        setCheckingComplianceFor(null);
        setComplianceError(errMsg || "Failed to check compliance. Please try again.");
      }
    );
  };

  return {
    purchaseOrder,
    items,
    totalWeight,
    isLoading,
    hasError,
    isPending,
    canAccept,
    canDispute,
    canGenerateUpc,
    documentUrl,
    phoneNumber,
    creatorName,
    goToList,
    goToPoDetails,
    downloadPurchaseOrder,
    callPurchaseOrderCreator,
    showContactDetails,
    setShowContactDetails,
    showConfirmation,
    setShowConfirmation,
    accepting: updateSellerApprovalStatus === STATUS_BEGIN,
    accepted,
    acceptPurchaseOrder,
    showDisputeForm,
    openDispute,
    closeDispute,
    dispute: {
      proposedDeliveryDate,
      setProposedDeliveryDate,
      proposedPrices,
      setProposedPrice,
      remarks,
      setRemarks,
      error: disputeError,
      submitting: submitPurchaseOrderDisputeStatus === STATUS_BEGIN,
      failed: submitPurchaseOrderDisputeStatus === STATUS_FAIL,
      submitted: showDisputeSubmittedModal,
      setSubmitted: setShowDisputeSubmittedModal,
      submit: submitDispute,
      onSuccessModalClose: onSuccessModalClose
    },
    handleGenerateQr,
    checkingComplianceFor,
    upcModalItem,
    closeUpcModal: () => setUpcModalItem(null),
    complianceError,
    clearComplianceError: () => setComplianceError(""),
  };
}
